import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "../utils/axiosInstance";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Toast,
  ToastContainer,
  InputGroup,
  Badge,
  OverlayTrigger,
  Tooltip,
  Pagination,
  Row,
  Col,
  Card,
  Offcanvas,
  Accordion,
  Collapse,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { getApiBase } from "../utils/useApiBase";
const API = getApiBase();

const ImiberehoMyiza = ({ isUmudugudu = false }) => {
  // ════════════════════════════════════════════════════════════════════════
  // STATES
  // ════════════════════════════════════════════════════════════════════════
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedItems, setSelectedItems] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  
  // Device & Responsive States
  const [deviceInfo, setDeviceInfo] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
    isLandscape: window.innerWidth > window.innerHeight,
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    isAndroid: /Android/.test(navigator.userAgent),
    isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    pixelRatio: window.devicePixelRatio || 1,
    touchDevice: 'ontouchstart' in window,
  });
  
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState('auto'); // 'auto', 'table', 'cards', 'list'
  const [expandedCard, setExpandedCard] = useState(null);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const containerRef = useRef(null);

  const [form, setForm] = useState({
    muturage_id: "",
    gahunda_yafashwemo: "",
    ibisobanuro: "",
    itariki_yatangiranye: "",
    imiterere_yayo: "Iri_gukorwa",
    amazina_y_umuturage: "",
  });

  // ════════════════════════════════════════════════════════════════════════
  // DEVICE DETECTION & RESPONSIVE HANDLER
  // ════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      const info = {
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isLandscape: width > height,
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isAndroid: /Android/.test(navigator.userAgent),
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        pixelRatio: window.devicePixelRatio || 1,
        touchDevice: 'ontouchstart' in window,
        // Specific device detection
        isIPhoneSE: width <= 375 && height <= 667,
        isIPhoneMini: width <= 375 && height > 667,
        isIPhoneStandard: width > 375 && width <= 390,
        isIPhoneMax: width > 390 && width <= 430,
        isSmallAndroid: width < 360,
        isMediumAndroid: width >= 360 && width < 400,
        isLargeAndroid: width >= 400 && width < 480,
        isPhablet: width >= 480 && width < 768,
      };
      
      setDeviceInfo(info);
      
      // Auto-adjust items per page
      if (width < 360) {
        setItemsPerPage(3);
      } else if (width < 480) {
        setItemsPerPage(5);
      } else if (width < 768) {
        setItemsPerPage(8);
      } else if (width < 1024) {
        setItemsPerPage(10);
      } else {
        setItemsPerPage(15);
      }
      
      // Auto-adjust view mode
      if (viewMode === 'auto') {
        if (width < 576) {
          // Will show cards
        } else if (width < 768) {
          // Will show compact cards
        } else {
          // Will show table
        }
      }
    };

    updateDeviceInfo();
    
    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', () => {
      setTimeout(updateDeviceInfo, 100);
    });
    
    // Handle visual viewport changes (for mobile keyboard)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateDeviceInfo);
    }
    
    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateDeviceInfo);
      }
    };
  }, [viewMode]);

  // ════════════════════════════════════════════════════════════════════════
  // THEME
  // ════════════════════════════════════════════════════════════════════════
  const theme = {
    bg: "#f0f4f8",
    cardBg: "#ffffff",
    cardBgHover: "#f0f4fb",
    text: "#0d1b2a",
    textMuted: "#64748b",
    primary: "#0B3D91",
    primaryDark: "#093278",
    success: "#16a34a",
    warning: "#d97706",
    danger: "#dc2626",
    info: "#0369a1",
    border: "#e2e8f0",
    tableBg: "#ffffff",
    tableHeaderBg: "#0B3D91",
    tableRowHover: "#eef3fb",
  };

  // ════════════════════════════════════════════════════════════════════════
  // HELPER FUNCTIONS
  // ════════════════════════════════════════════════════════════════════════
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: "" });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const url = isUmudugudu
        ? `${API}/umudugudu/module-data/imibereho`
        : `${API}/imibereho`;
      const res = await axios.get(url);
      setData(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch (err) {
      showToast("Habaye ikosa mu kugira amakuru", "error");
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  // Citizens already registered under Abaturage, for the auto-populate
  // dropdown below instead of hand-typing a name/ID.
  const [citizens, setCitizens] = useState([]);
  useEffect(() => {
    axios.get(`${API}/isibo/citizens`)
      .then((res) => setCitizens(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCitizens([]));
  }, []);

  const handleCitizenSelect = (e) => {
    const citizenId = e.target.value;
    const citizen = citizens.find((c) => String(c.umuturage_id) === String(citizenId));
    setForm((prev) => ({
      ...prev,
      muturage_id: citizenId,
      amazina_y_umuturage: citizen ? citizen.full_name : "",
    }));
    if (formErrors.muturage_id) setFormErrors((prev) => ({ ...prev, muturage_id: "", amazina_y_umuturage: "" }));
  };

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3500);
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!form.amazina_y_umuturage.trim()) errors.amazina_y_umuturage = "Amazina ni ngombwa";
    if (!form.muturage_id.trim()) errors.muturage_id = "ID ni ngombwa";
    if (!form.gahunda_yafashwemo.trim()) errors.gahunda_yafashwemo = "Gahunda ni ngombwa";
    if (!form.itariki_yatangiranye) errors.itariki_yatangiranye = "Itariki ni ngombwa";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const filteredData = useMemo(() => {
    let filtered = [...data];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.amazina_y_umuturage?.toLowerCase().includes(term) ||
        item.gahunda_yafashwemo?.toLowerCase().includes(term) ||
        item.muturage_id?.toLowerCase().includes(term) ||
        item.ibisobanuro?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.imiterere_yayo === statusFilter);
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const va = a[sortConfig.key] || '';
        const vb = b[sortConfig.key] || '';
        if (va < vb) return sortConfig.direction === "asc" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchTerm, statusFilter, sortConfig]);

  const stats = useMemo(() => {
    const total = data.length;
    const completed = data.filter(item => item.imiterere_yayo === "Yarangiye").length;
    const inProgress = data.filter(item => item.imiterere_yayo === "Iri_gukorwa").length;
    const filtered = filteredData.length;

    return { total, completed, inProgress, filtered };
  }, [data, filteredData]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc"
    });
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm({
      muturage_id: "",
      gahunda_yafashwemo: "",
      ibisobanuro: "",
      itariki_yatangiranye: "",
      imiterere_yayo: "Iri_gukorwa",
      amazina_y_umuturage: "",
    });
    setFormErrors({});
    setShow(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.imibereho_id);
    setForm({
      ...item,
      muturage_id: item.umuturage_id ?? item.muturage_id ?? "",
      itariki_yatangiranye: item.itariki_yatangiranye ? item.itariki_yatangiranye.split('T')[0] : ""
    });
    setFormErrors({});
    setShow(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/imibereho/${deleteId}`);
      showToast("Ibyanditswe byasibwe neza!", "success");
      loadData();
      setShowDeleteConfirm(false);
      setDeleteId(null);
    } catch (err) {
      showToast("Habaye ikosa mu gusiba", "error");
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Urashaka gusiba ${selectedItems.length} ibintu koko?`)) return;
    try {
      await Promise.all(selectedItems.map(id => axios.delete(`${API}/imibereho/${id}`)));
      showToast(`${selectedItems.length} ibintu byasibwe neza!`, "success");
      setSelectedItems([]);
      loadData();
    } catch (err) {
      showToast("Habaye ikosa mu gusiba ibintu", "error");
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === paginatedData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(paginatedData.map(item => item.imibereho_id));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showToast("Uzuza neza amakuru yose!", "error");
      return;
    }
    try {
      if (editingId === null) {
        await axios.post(`${API}/imibereho`, form);
        showToast("Ibyanditswe byongewe neza!", "success");
      } else {
        await axios.put(`${API}/imibereho/${editingId}`, form);
        showToast("Ibyanditswe byavuguruwe neza!", "success");
      }
      setShow(false);
      loadData();
    } catch (err) {
      showToast("Habaye ikosa mu kubika", "error");
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const getStatusBadge = (status) => {
    const badges = {
      "Yarangiye": <Badge bg="success" className="status-badge">✓ Yarangiye</Badge>,
      "Iri_gukorwa": <Badge bg="warning" text="dark" className="status-badge">⏳ Iri gukorwa</Badge>,
    };
    return badges[status] || <Badge bg="secondary" className="status-badge">{status || "—"}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("rw-RW", {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const exportToCSV = () => {
    const headers = ["No", "Amazina", "ID", "Gahunda", "Itariki", "Imiterere", "Ibisobanuro"];
    const csvContent = [
      headers.join(","),
      ...filteredData.map((item, index) => [
        index + 1,
        `"${item.amazina_y_umuturage || ''}"`,
        `"${item.muturage_id || ''}"`,
        `"${item.gahunda_yafashwemo || ''}"`,
        `"${item.itariki_yatangiranye || ''}"`,
        `"${item.imiterere_yayo || ''}"`,
        `"${(item.ibisobanuro || '').replace(/"/g, '""')}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `imibereho_myiza_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDER COMPONENTS
  // ════════════════════════════════════════════════════════════════════════
  
  // Ultra Compact Cards for Very Small Screens (< 360px)
  const renderUltraCompactCards = () => (
    <div className="ultra-compact-cards">
      {paginatedData.map((item, index) => (
        <div key={item.imibereho_id} className="ultra-compact-card">
          <div className="ucc-header">
            <Form.Check
              type="checkbox"
              checked={selectedItems.includes(item.imibereho_id)}
              onChange={() => handleSelectItem(item.imibereho_id)}
              className="ucc-checkbox"
            />
            <div className="ucc-main">
              <span className="ucc-number">#{startIndex + index + 1}</span>
              <h6 className="ucc-name">{item.amazina_y_umuturage}</h6>
            </div>
            {getStatusBadge(item.imiterere_yayo)}
          </div>
          
          <div 
            className="ucc-body"
            onClick={() => setExpandedCard(expandedCard === item.imibereho_id ? null : item.imibereho_id)}
          >
            <div className="ucc-row">
              <span className="ucc-label">ID:</span>
              <span className="ucc-value code-tag">{item.muturage_id}</span>
            </div>
            <div className="ucc-row">
              <span className="ucc-label">Gahunda:</span>
              <span className="ucc-value">{item.gahunda_yafashwemo}</span>
            </div>
            
            <Collapse in={expandedCard === item.imibereho_id}>
              <div className="ucc-expanded">
                <div className="ucc-row">
                  <span className="ucc-label">Itariki:</span>
                  <span className="ucc-value">{formatDate(item.itariki_yatangiranye)}</span>
                </div>
                {item.ibisobanuro && (
                  <div className="ucc-description">
                    <span className="ucc-label">Ibisobanuro:</span>
                    <p className="ucc-desc-text">{item.ibisobanuro}</p>
                  </div>
                )}
              </div>
            </Collapse>
            
            <div className="ucc-expand-hint">
              <i className={`bi bi-chevron-${expandedCard === item.imibereho_id ? 'up' : 'down'}`}></i>
            </div>
          </div>
          
          <div className="ucc-actions">
            <button className="ucc-btn edit" onClick={() => handleEdit(item)}>
              <i className="bi bi-pencil"></i>
            </button>
            <button className="ucc-btn delete" onClick={() => handleDeleteClick(item.imibereho_id)}>
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // Mobile Cards for Small to Medium Phones (360px - 575px)
  const renderMobileCards = () => (
    <div className="mobile-cards-container">
      {paginatedData.map((item, index) => (
        <div key={item.imibereho_id} className="mobile-data-card">
          <div className="mobile-card-header">
            <div className="mobile-card-left">
              <Form.Check
                type="checkbox"
                checked={selectedItems.includes(item.imibereho_id)}
                onChange={() => handleSelectItem(item.imibereho_id)}
              />
              <span className="mobile-card-number">#{startIndex + index + 1}</span>
            </div>
            <div className="mobile-card-title">
              <h6>{item.amazina_y_umuturage}</h6>
              <span className="code-tag">{item.muturage_id}</span>
            </div>
            {getStatusBadge(item.imiterere_yayo)}
          </div>
          
          <div className="mobile-card-body">
            <div className="mobile-card-grid">
              <div className="mobile-card-field">
                <span className="field-label">
                  <i className="bi bi-clipboard-check"></i> Ikibazo
                </span>
                <span className="field-value">{item.gahunda_yafashwemo}</span>
              </div>
              <div className="mobile-card-field">
                <span className="field-label">
                  <i className="bi bi-calendar3"></i> Itariki
                </span>
                <span className="field-value">{formatDate(item.itariki_yatangiranye)}</span>
              </div>
            </div>
            
            {item.ibisobanuro && (
              <div className="mobile-card-description">
                <span className="field-label">
                  <i className="bi bi-info-circle"></i> Ibisobanuro
                </span>
                <p className="desc-text">{item.ibisobanuro}</p>
              </div>
            )}
          </div>
          
          <div className="mobile-card-actions">
            <Button className="mobile-action-btn edit" onClick={() => handleEdit(item)}>
              <i className="bi bi-pencil-square"></i>
              <span>Hindura</span>
            </Button>
            <Button className="mobile-action-btn delete" onClick={() => handleDeleteClick(item.imibereho_id)}>
              <i className="bi bi-trash"></i>
              <span>Siba</span>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  // Tablet List View (576px - 767px)
  const renderTabletList = () => (
    <div className="tablet-list-container">
      <div className="tablet-list-header">
        <Form.Check
          type="checkbox"
          checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
          onChange={handleSelectAll}
          label={<span className="select-all-label">Hitamo byose</span>}
        />
        <div className="tablet-list-sort">
          <Button 
            variant="link" 
            className="sort-btn"
            onClick={() => handleSort("amazina_y_umuturage")}
          >
            <i className="bi bi-sort-alpha-down"></i> Amazina
          </Button>
          <Button 
            variant="link" 
            className="sort-btn"
            onClick={() => handleSort("itariki_yatangiranye")}
          >
            <i className="bi bi-sort-numeric-down"></i> Itariki
          </Button>
        </div>
      </div>
      
      {paginatedData.map((item, index) => (
        <div key={item.imibereho_id} className="tablet-list-item">
          <div className="tli-left">
            <Form.Check
              type="checkbox"
              checked={selectedItems.includes(item.imibereho_id)}
              onChange={() => handleSelectItem(item.imibereho_id)}
            />
            <span className="tli-number">{startIndex + index + 1}</span>
          </div>
          
          <div className="tli-content">
            <div className="tli-top">
              <h6 className="tli-name">{item.amazina_y_umuturage}</h6>
              {getStatusBadge(item.imiterere_yayo)}
            </div>
            <div className="tli-details">
              <span className="tli-detail">
                <i className="bi bi-person-badge"></i>
                <span className="code-tag">{item.muturage_id}</span>
              </span>
              <span className="tli-detail">
                <i className="bi bi-clipboard"></i>
                {item.gahunda_yafashwemo}
              </span>
              <span className="tli-detail">
                <i className="bi bi-calendar"></i>
                {formatDate(item.itariki_yatangiranye)}
              </span>
            </div>
            {item.ibisobanuro && (
              <p className="tli-description">{item.ibisobanuro}</p>
            )}
          </div>
          
          <div className="tli-actions">
            <Button 
              variant="outline-primary" 
              size="sm"
              className="tli-btn"
              onClick={() => handleEdit(item)}
            >
              <i className="bi bi-pencil"></i>
            </Button>
            <Button 
              variant="outline-danger" 
              size="sm"
              className="tli-btn"
              onClick={() => handleDeleteClick(item.imibereho_id)}
            >
              <i className="bi bi-trash"></i>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  // Full Desktop Table (768px+)
  const renderDesktopTable = () => (
    <div className="table-responsive">
      <Table hover className="modern-table">
        <thead>
          <tr>
            <th style={{ width: '50px' }}>
              <Form.Check
                type="checkbox"
                checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                onChange={handleSelectAll}
              />
            </th>
            <th style={{ width: '50px' }}>#</th>
            <th className="sortable" onClick={() => handleSort("amazina_y_umuturage")}>
              Amazina
              {sortConfig.key === "amazina_y_umuturage" && (
                <i className={`bi bi-caret-${sortConfig.direction === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
              )}
            </th>
            <th>ID / Nomero</th>
            <th>Ikibazo / Gahunda</th>
            <th className="sortable" onClick={() => handleSort("itariki_yatangiranye")}>
              Itariki
              {sortConfig.key === "itariki_yatangiranye" && (
                <i className={`bi bi-caret-${sortConfig.direction === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
              )}
            </th>
            <th>Imiterere</th>
            <th className={showAllColumns ? '' : 'd-none d-xl-table-cell'}>Ibisobanuro</th>
            <th className="text-center" style={{ width: '120px' }}>Ibikorwa</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item, index) => (
            <tr key={item.imibereho_id} className={selectedItems.includes(item.imibereho_id) ? 'selected-row' : ''}>
              <td>
                <Form.Check
                  type="checkbox"
                  checked={selectedItems.includes(item.imibereho_id)}
                  onChange={() => handleSelectItem(item.imibereho_id)}
                />
              </td>
              <td className="row-number">{startIndex + index + 1}</td>
              <td>
                <div className="name-cell">
                  <span className="name-text">{item.amazina_y_umuturage}</span>
                </div>
              </td>
              <td>
                <span className="code-tag">{item.muturage_id}</span>
              </td>
              <td>
                <span className="gahunda-text">{item.gahunda_yafashwemo}</span>
              </td>
              <td className="date-cell">
                {formatDate(item.itariki_yatangiranye)}
              </td>
              <td>{getStatusBadge(item.imiterere_yayo)}</td>
              <td className={showAllColumns ? '' : 'd-none d-xl-table-cell'}>
                <OverlayTrigger 
                  placement="top" 
                  overlay={<Tooltip>{item.ibisobanuro || "Nta bisobanuro"}</Tooltip>}
                >
                  <span className="description-cell">
                    {item.ibisobanuro || "—"}
                  </span>
                </OverlayTrigger>
              </td>
              <td>
                <div className="action-buttons">
                  <OverlayTrigger placement="top" overlay={<Tooltip>Hindura</Tooltip>}>
                    <Button 
                      className="btn-action btn-outline-primary" 
                      onClick={() => handleEdit(item)}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Siba</Tooltip>}>
                    <Button 
                      className="btn-action btn-outline-danger" 
                      onClick={() => handleDeleteClick(item.imibereho_id)}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  // Determine which view to render
  const renderDataView = () => {
    const { width } = deviceInfo;
    
    if (loading) {
      return (
        <div className="loading-state">
          <Spinner animation="border" style={{ color: theme.primary }} />
          <p>Ibyanditswe biragarurwa...</p>
        </div>
      );
    }
    
    if (filteredData.length === 0) {
      return (
        <div className="empty-state">
          <i className="bi bi-inbox"></i>
          <h4>Nta mishinga ihari</h4>
          <p>
            {searchTerm || statusFilter !== "all"
              ? "Nta makuru ahuye n'ibyo ushakisha."
              : "Tangira ukongeramo umushinga wa mbere."}
          </p>
          <Button className="btn-primary-modern" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>
            Ongeramo umushinga
          </Button>
        </div>
      );
    }

    // Force specific view if not auto
    if (viewMode === 'table' && width >= 576) {
      return renderDesktopTable();
    }
    if (viewMode === 'cards') {
      return width < 360 ? renderUltraCompactCards() : renderMobileCards();
    }
    if (viewMode === 'list' && width >= 576) {
      return renderTabletList();
    }

    // Auto view based on screen size
    if (width < 360) {
      return renderUltraCompactCards();
    } else if (width < 576) {
      return renderMobileCards();
    } else if (width < 768) {
      return renderTabletList();
    } else {
      return renderDesktopTable();
    }
  };

  // Render Statistics Cards
  const renderStats = () => {
    const statItems = [
      { 
        title: deviceInfo.width < 400 ? "Byose" : "Byose Hamwe", 
        val: stats.total, 
        color: theme.primary, 
        icon: "list-check",
        percent: 100 
      },
      { 
        title: deviceInfo.width < 400 ? "Byarangiye" : "Byarangiye", 
        val: stats.completed, 
        color: theme.success, 
        icon: "check-circle-fill",
        percent: stats.total ? Math.round((stats.completed / stats.total) * 100) : 0 
      },
      { 
        title: deviceInfo.width < 400 ? "Biracyakorwa" : "Biracyakorwa", 
        val: stats.inProgress, 
        color: theme.warning, 
        icon: "hourglass-split",
        percent: stats.total ? Math.round((stats.inProgress / stats.total) * 100) : 0 
      },
      { 
        title: deviceInfo.width < 400 ? "Byabonetse" : "Ibyabonetse", 
        val: stats.filtered, 
        color: theme.info, 
        icon: "funnel-fill",
        percent: stats.total ? Math.round((stats.filtered / stats.total) * 100) : 0 
      },
    ];

    return (
      <div className="stats-grid">
        {statItems.map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ '--stat-color': s.color }}>
              <i className={`bi bi-${s.icon}`}></i>
            </div>
            <div className="stat-content">
              <span className="stat-value">{s.val}</span>
              <span className="stat-label">{s.title}</span>
              <span className="stat-percent" style={{ color: s.color }}>{s.percent}%</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render Pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const { width } = deviceInfo;
    const maxVisiblePages = width < 400 ? 3 : width < 576 ? 5 : 7;
    
    let pages = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <span className="showing-text">
            {width < 400 ? `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredData.length)}` : 
              `Kuranga ${startIndex + 1} - ${Math.min(startIndex + itemsPerPage, filteredData.length)} muri ${filteredData.length}`}
          </span>
        </div>
        
        <Pagination className="custom-pagination">
          <Pagination.First 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
            className="d-none d-sm-block"
          />
          <Pagination.Prev 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          />
          
          {startPage > 1 && (
            <>
              <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
              {startPage > 2 && <Pagination.Ellipsis disabled />}
            </>
          )}
          
          {pages.map(page => (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </Pagination.Item>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
              <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
                {totalPages}
              </Pagination.Item>
            </>
          )}
          
          <Pagination.Next 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          />
          <Pagination.Last 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
            className="d-none d-sm-block"
          />
        </Pagination>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div 
      ref={containerRef}
      className={`app-container ${deviceInfo.isIOS ? 'ios-device' : ''} ${deviceInfo.isAndroid ? 'android-device' : ''}`}
      style={{ background: theme.bg, color: theme.text }}
    >

      {/* ══════════════════════════════════════════════════════════════════
          COMPREHENSIVE RESPONSIVE STYLES
      ══════════════════════════════════════════════════════════════════ */}
      <style>{`
        /* ═══════════════════════════════════════════════════════════════════
           CSS CUSTOM PROPERTIES (DESIGN TOKENS)
        ═══════════════════════════════════════════════════════════════════ */
        :root {
          --color-bg: #ffffff;
          --color-card: #ffffff;
          --color-card-hover: #f0f4fb;
          --color-text: #0d1b2a;
          --color-text-muted: #64748b;
          --color-primary: #0B3D91;
          --color-primary-dark: #093278;
          --color-success: #16a34a;
          --color-warning: #d97706;
          --color-danger: #dc2626;
          --color-info: #0369a1;
          --color-border: #e2e8f0;
          
          --radius-sm: 6px;
          --radius-md: 10px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          
          --shadow-sm: 0 1px 2px rgba(0,0,0,0.3);
          --shadow-md: 0 4px 12px rgba(0,0,0,0.4);
          --shadow-lg: 0 10px 25px rgba(0,0,0,0.5);
          
          --font-xs: clamp(0.65rem, 2vw, 0.75rem);
          --font-sm: clamp(0.75rem, 2.5vw, 0.85rem);
          --font-md: clamp(0.85rem, 3vw, 1rem);
          --font-lg: clamp(1rem, 3.5vw, 1.25rem);
          --font-xl: clamp(1.25rem, 4vw, 1.5rem);
          --font-2xl: clamp(1.5rem, 5vw, 2rem);
          
          --space-xs: clamp(0.25rem, 1vw, 0.5rem);
          --space-sm: clamp(0.5rem, 2vw, 0.75rem);
          --space-md: clamp(0.75rem, 3vw, 1rem);
          --space-lg: clamp(1rem, 4vw, 1.5rem);
          --space-xl: clamp(1.5rem, 5vw, 2rem);
        }

        /* ═══════════════════════════════════════════════════════════════════
           BASE STYLES & RESET
        ═══════════════════════════════════════════════════════════════════ */
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');
        
        *, *::before, *::after {
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none;
        }

        html {
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
          text-size-adjust: 100%;
          scroll-behavior: smooth;
        }

        body {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          
          touch-action: pan-y;
        }

        /* ═══════════════════════════════════════════════════════════════════
           APP CONTAINER - SAFE AREAS FOR ALL DEVICES
        ═══════════════════════════════════════════════════════════════════ */
        .app-container {
          min-height: 100vh;
          min-height: 100dvh;
          min-height: 100dvh;
          padding: var(--space-md);
          padding-top: max(var(--space-md), env(safe-area-inset-top));
          padding-bottom: max(var(--space-md), env(safe-area-inset-bottom));
          padding-left: max(var(--space-md), env(safe-area-inset-left));
          padding-right: max(var(--space-md), env(safe-area-inset-right));
          overflow-x: hidden;
          position: relative;
        }

        .app-container.ios-device {
          /* iOS specific adjustments */
          -webkit-overflow-scrolling: touch;
        }

        .app-container.android-device {
          /* Android specific adjustments */
        }

        /* ═══════════════════════════════════════════════════════════════════
           HEADER SECTION
        ═══════════════════════════════════════════════════════════════════ */
        .header-section {
          margin-bottom: var(--space-md);
        }

        .header-top {
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }

        .header-title {
          font-size: var(--font-lg);
          font-weight: 700;
          color: var(--color-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          line-height: 1.3;
          margin: 0;
        }

        .header-title i {
          font-size: 1.2em;
        }

        .header-subtitle {
          font-size: var(--font-sm);
          color: var(--color-text-muted);
          line-height: 1.4;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: var(--space-xs);
          flex-wrap: wrap;
          margin-top: var(--space-sm);
        }

        /* ═══════════════════════════════════════════════════════════════════
           STATISTICS GRID
        ═══════════════════════════════════════════════════════════════════ */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-sm);
          margin-bottom: var(--space-md);
        }

        .stat-card {
          background: linear-gradient(145deg, #ffffff 0%, #f0f4fb 100%);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-sm);
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          border-color: var(--color-primary);
          transform: translateY(-2px);
        }

        .stat-icon {
          width: clamp(36px, 10vw, 50px);
          height: clamp(36px, 10vw, 50px);
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-icon i {
          font-size: clamp(1rem, 4vw, 1.5rem);
          color: var(--stat-color, var(--color-primary));
        }

        .stat-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: clamp(1.25rem, 5vw, 1.75rem);
          font-weight: 800;
          color: var(--color-text);
          line-height: 1.1;
        }

        .stat-label {
          font-size: var(--font-xs);
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .stat-percent {
          font-size: var(--font-xs);
          font-weight: 600;
        }

        /* ═══════════════════════════════════════════════════════════════════
           FILTER SECTION
        ═══════════════════════════════════════════════════════════════════ */
        .filter-section {
          margin-bottom: var(--space-md);
        }

        .filter-card {
          background: linear-gradient(145deg, #ffffff 0%, #f0f4fb 100%);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-sm);
        }

        .search-container {
          display: flex;
          gap: var(--space-xs);
          flex-wrap: wrap;
        }

        .search-input-wrapper {
          flex: 1;
          min-width: 150px;
          position: relative;
        }

        .search-input-wrapper .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-text-muted);
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          background: #ffffff !important;
          border: 1px solid var(--color-border) !important;
          color: var(--color-text) !important;
          border-radius: var(--radius-sm) !important;
          padding: 0.6rem 0.75rem 0.6rem 2.25rem !important;
          font-size: var(--font-sm) !important;
        }

        .search-input:focus {
          border-color: var(--color-primary) !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
        }

        .search-input::placeholder {
          color: #64748b !important;
        }

        .filter-select {
          background: #ffffff !important;
          border: 1px solid var(--color-border) !important;
          color: var(--color-text) !important;
          border-radius: var(--radius-sm) !important;
          padding: 0.6rem 2rem 0.6rem 0.75rem !important;
          font-size: var(--font-sm) !important;
          min-width: 100px;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23a1a1aa' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important;
          background-repeat: no-repeat !important;
          background-position: right 0.5rem center !important;
          background-size: 12px !important;
        }

        .filter-select:focus {
          border-color: var(--color-primary) !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
        }

        .mobile-filter-btn {
          display: none;
        }

        /* ═══════════════════════════════════════════════════════════════════
           BULK ACTIONS BAR
        ═══════════════════════════════════════════════════════════════════ */
        .bulk-bar {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0.06) 100%);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: var(--radius-md);
          padding: var(--space-sm) var(--space-md);
          margin-bottom: var(--space-md);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .bulk-info {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          font-size: var(--font-sm);
        }

        .bulk-actions {
          display: flex;
          gap: var(--space-xs);
        }

        /* ═══════════════════════════════════════════════════════════════════
           DATA CONTAINER
        ═══════════════════════════════════════════════════════════════════ */
        .data-container {
          background: #ffffff;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-lg);
          overflow: hidden;
        }

        .data-header {
          background: linear-gradient(180deg, #0B3D91 0%, #ffffff 100%);
          padding: var(--space-sm) var(--space-md);
          border-bottom: 1px solid var(--color-border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }

        .data-header-title {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          font-size: var(--font-md);
          font-weight: 600;
          color: var(--color-text);
          margin: 0;
        }

        .data-header-title i {
          color: var(--color-primary);
        }

        .data-header-meta {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .results-count {
          font-size: var(--font-xs);
          color: var(--color-text-muted);
        }

        .view-toggle {
          display: none;
          gap: 2px;
          background: #f0f4fb;
          padding: 3px;
          border-radius: var(--radius-sm);
        }

        .view-toggle-btn {
          background: transparent;
          border: none;
          color: var(--color-text-muted);
          padding: 0.35rem 0.5rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: var(--font-sm);
        }

        .view-toggle-btn.active {
          background: var(--color-primary);
          color: white;
        }

        /* ═══════════════════════════════════════════════════════════════════
           LOADING & EMPTY STATES
        ═══════════════════════════════════════════════════════════════════ */
        .loading-state,
        .empty-state {
          padding: var(--space-xl);
          text-align: center;
        }

        .loading-state p,
        .empty-state h4 {
          color: var(--color-text-muted);
          margin-top: var(--space-md);
        }

        .empty-state i {
          font-size: clamp(3rem, 15vw, 5rem);
          color: #e2e8f0;
        }

        .empty-state p {
          color: #64748b;
          margin-bottom: var(--space-lg);
        }

        /* ═══════════════════════════════════════════════════════════════════
           ULTRA COMPACT CARDS (< 360px)
        ═══════════════════════════════════════════════════════════════════ */
        .ultra-compact-cards {
          padding: var(--space-xs);
        }

        .ultra-compact-card {
          background: linear-gradient(145deg, #ffffff 0%, #f0f4fb 100%);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-xs);
          overflow: hidden;
        }

        .ucc-header {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          padding: var(--space-xs) var(--space-sm);
          background: rgba(255,255,255,0.02);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .ucc-checkbox .form-check-input {
          width: 16px;
          height: 16px;
        }

        .ucc-main {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: var(--space-xs);
        }

        .ucc-number {
          font-size: 0.65rem;
          color: var(--color-text-muted);
          background: rgba(255,255,255,0.05);
          padding: 0.1rem 0.35rem;
          border-radius: 4px;
        }

        .ucc-name {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-text);
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ucc-body {
          padding: var(--space-xs) var(--space-sm);
          cursor: pointer;
        }

        .ucc-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 0.25rem 0;
          gap: var(--space-xs);
        }

        .ucc-label {
          font-size: 0.65rem;
          color: var(--color-text-muted);
          flex-shrink: 0;
        }

        .ucc-value {
          font-size: 0.75rem;
          color: var(--color-text);
          text-align: right;
          word-break: break-word;
        }

        .ucc-expanded {
          padding-top: var(--space-xs);
          border-top: 1px dashed rgba(255,255,255,0.1);
          margin-top: var(--space-xs);
        }

        .ucc-description {
          margin-top: var(--space-xs);
        }

        .ucc-desc-text {
          font-size: 0.7rem;
          color: var(--color-text-muted);
          line-height: 1.4;
          margin: 0.25rem 0 0 0;
        }

        .ucc-expand-hint {
          text-align: center;
          color: var(--color-text-muted);
          font-size: 0.7rem;
          padding-top: var(--space-xs);
        }

        .ucc-actions {
          display: flex;
          border-top: 1px solid var(--color-border);
        }

        .ucc-btn {
          flex: 1;
          border: none;
          padding: 0.5rem;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ucc-btn.edit {
          background: rgba(99, 102, 241, 0.1);
          color: #0B3D91;
        }

        .ucc-btn.delete {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border-left: 1px solid var(--color-border);
        }

        .ucc-btn:active {
          opacity: 0.7;
        }

        /* ═══════════════════════════════════════════════════════════════════
           MOBILE CARDS (360px - 575px)
        ═══════════════════════════════════════════════════════════════════ */
        .mobile-cards-container {
          padding: var(--space-sm);
        }

        .mobile-data-card {
          background: linear-gradient(145deg, #ffffff 0%, #f0f4fb 100%);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-lg);
          margin-bottom: var(--space-sm);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .mobile-data-card:active {
          transform: scale(0.98);
        }

        .mobile-card-header {
          display: flex;
          align-items: flex-start;
          gap: var(--space-sm);
          padding: var(--space-sm);
          background: linear-gradient(180deg, #f0f4fb 0%, transparent 100%);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .mobile-card-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .mobile-card-number {
          font-size: 0.6rem;
          color: var(--color-text-muted);
        }

        .mobile-card-title {
          flex: 1;
          min-width: 0;
        }

        .mobile-card-title h6 {
          font-size: var(--font-sm);
          font-weight: 600;
          color: var(--color-text);
          margin: 0 0 0.25rem 0;
          line-height: 1.3;
        }

        .mobile-card-body {
          padding: var(--space-sm);
        }

        .mobile-card-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-sm);
        }

        .mobile-card-field {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }

        .field-label {
          font-size: var(--font-xs);
          color: var(--color-text-muted);
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .field-value {
          font-size: var(--font-sm);
          color: var(--color-text);
          word-break: break-word;
        }

        .mobile-card-description {
          margin-top: var(--space-sm);
          padding-top: var(--space-sm);
          border-top: 1px dashed rgba(255,255,255,0.1);
        }

        .desc-text {
          font-size: var(--font-xs);
          color: var(--color-text-muted);
          line-height: 1.5;
          margin: 0.25rem 0 0 0;
        }

        .mobile-card-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: var(--color-border);
        }

        .mobile-action-btn {
          border: none !important;
          border-radius: 0 !important;
          padding: 0.6rem !important;
          font-size: var(--font-sm) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 0.35rem !important;
          transition: all 0.2s !important;
        }

        .mobile-action-btn.edit {
          background: rgba(99, 102, 241, 0.1) !important;
          color: #0B3D91 !important;
        }

        .mobile-action-btn.delete {
          background: rgba(239, 68, 68, 0.1) !important;
          color: #dc2626 !important;
        }

        .mobile-action-btn:active {
          opacity: 0.7 !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           TABLET LIST VIEW (576px - 767px)
        ═══════════════════════════════════════════════════════════════════ */
        .tablet-list-container {
          padding: var(--space-sm);
        }

        .tablet-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-sm);
          background: rgba(255,255,255,0.02);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-sm);
        }

        .select-all-label {
          font-size: var(--font-xs);
          color: var(--color-text-muted);
          margin-left: 0.5rem;
        }

        .tablet-list-sort {
          display: flex;
          gap: var(--space-xs);
        }

        .sort-btn {
          font-size: var(--font-xs) !important;
          color: var(--color-text-muted) !important;
          padding: 0.25rem 0.5rem !important;
          text-decoration: none !important;
        }

        .sort-btn:hover {
          color: var(--color-primary) !important;
        }

        .tablet-list-item {
          display: flex;
          gap: var(--space-sm);
          padding: var(--space-sm);
          background: linear-gradient(145deg, #ffffff 0%, #f0f4fb 100%);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-xs);
        }

        .tli-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          padding-top: 0.15rem;
        }

        .tli-number {
          font-size: 0.65rem;
          color: var(--color-text-muted);
        }

        .tli-content {
          flex: 1;
          min-width: 0;
        }

        .tli-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-xs);
          margin-bottom: 0.35rem;
        }

        .tli-name {
          font-size: var(--font-sm);
          font-weight: 600;
          color: var(--color-text);
          margin: 0;
        }

        .tli-details {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .tli-detail {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: var(--font-xs);
          color: var(--color-text-muted);
        }

        .tli-detail i {
          font-size: 0.8em;
          opacity: 0.7;
        }

        .tli-description {
          font-size: var(--font-xs);
          color: var(--color-text-muted);
          margin: 0.5rem 0 0 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .tli-actions {
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
        }

        .tli-btn {
          width: 36px !important;
          height: 36px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: var(--radius-sm) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           DESKTOP TABLE (768px+)
        ═══════════════════════════════════════════════════════════════════ */
        .modern-table {
          background-color: #ffffff !important;
          color: var(--color-text) !important;
          margin-bottom: 0 !important;
        }

        .modern-table thead {
          background: linear-gradient(180deg, #0B3D91 0%, #ffffff 100%) !important;
        }

        .modern-table thead th {
          background: transparent !important;
          color: var(--color-text-muted) !important;
          font-weight: 600 !important;
          font-size: var(--font-xs) !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          padding: var(--space-sm) !important;
          border-bottom: 1px solid var(--color-border) !important;
          white-space: nowrap !important;
          vertical-align: middle !important;
        }

        .modern-table thead th.sortable {
          cursor: pointer;
          user-select: none;
          transition: all 0.2s;
        }

        .modern-table thead th.sortable:hover {
          color: var(--color-primary) !important;
          background: rgba(99, 102, 241, 0.05) !important;
        }

        .modern-table tbody tr {
          transition: all 0.2s ease !important;
          border-bottom: 1px solid rgba(255,255,255,0.05) !important;
        }

        .modern-table tbody tr:hover {
          background: rgba(99, 102, 241, 0.05) !important;
        }

        .modern-table tbody tr.selected-row {
          background: rgba(99, 102, 241, 0.1) !important;
        }

        .modern-table tbody tr:last-child {
          border-bottom: none !important;
        }

        .modern-table tbody td {
          background: transparent !important;
          color: var(--color-text) !important;
          padding: var(--space-sm) !important;
          vertical-align: middle !important;
          border: none !important;
          font-size: var(--font-sm) !important;
        }

        .row-number {
          color: var(--color-text-muted);
          font-size: var(--font-xs);
        }

        .name-cell .name-text {
          font-weight: 500;
        }

        .gahunda-text {
          max-width: 200px;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .date-cell {
          color: var(--color-text-muted);
          white-space: nowrap;
        }

        .description-cell {
          max-width: 180px;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: var(--color-text-muted);
        }

        .action-buttons {
          display: flex;
          gap: var(--space-xs);
          justify-content: center;
        }

        /* ═══════════════════════════════════════════════════════════════════
           COMMON ELEMENTS
        ═══════════════════════════════════════════════════════════════════ */
        .code-tag {
          font-family: 'JetBrains Mono', 'Fira Code', monospace;
          font-size: 0.75em;
          color: #0369a1;
          background: rgba(34, 211, 238, 0.1);
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          display: inline-block;
        }

        .status-badge {
          font-size: var(--font-xs) !important;
          padding: 0.3rem 0.5rem !important;
          border-radius: 6px !important;
          font-weight: 500 !important;
          white-space: nowrap;
        }

        .form-check-input {
          background-color: #f0f4fb;
          border-color: #cbd5e1;
          cursor: pointer;
        }

        .form-check-input:checked {
          background-color: var(--color-primary);
          border-color: var(--color-primary);
        }

        .form-check-input:focus {
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
        }

        /* ═══════════════════════════════════════════════════════════════════
           BUTTONS
        ═══════════════════════════════════════════════════════════════════ */
        .btn-primary-modern {
          background: linear-gradient(135deg, #0B3D91 0%, #093278 100%) !important;
          border: none !important;
          border-radius: var(--radius-md) !important;
          padding: var(--space-sm) var(--space-md) !important;
          font-weight: 600 !important;
          font-size: var(--font-sm) !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 0.4rem !important;
          white-space: nowrap !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3) !important;
        }

        .btn-primary-modern:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4) !important;
        }

        .btn-primary-modern:active {
          transform: scale(0.96) !important;
        }

        .btn-outline-modern {
          background: transparent !important;
          border: 1px solid var(--color-border) !important;
          color: var(--color-text-muted) !important;
          border-radius: var(--radius-sm) !important;
          padding: var(--space-xs) var(--space-sm) !important;
          font-size: var(--font-sm) !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 0.3rem !important;
          transition: all 0.3s ease !important;
        }

        .btn-outline-modern:hover {
          background: #f0f4fb !important;
          border-color: var(--color-primary) !important;
          color: var(--color-text) !important;
        }

        .btn-outline-modern:active {
          transform: scale(0.95) !important;
        }

        .btn-action {
          width: 34px !important;
          height: 34px !important;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: var(--radius-sm) !important;
          transition: all 0.2s ease !important;
        }

        .btn-action:active {
          transform: scale(0.9) !important;
        }

        .btn-action.btn-outline-primary {
          border-color: var(--color-primary) !important;
          color: var(--color-primary) !important;
        }

        .btn-action.btn-outline-primary:hover {
          background: rgba(99, 102, 241, 0.15) !important;
        }

        .btn-action.btn-outline-danger {
          border-color: var(--color-danger) !important;
          color: var(--color-danger) !important;
        }

        .btn-action.btn-outline-danger:hover {
          background: rgba(239, 68, 68, 0.15) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           PAGINATION
        ═══════════════════════════════════════════════════════════════════ */
        .pagination-wrapper {
          padding: var(--space-sm) var(--space-md);
          border-top: 1px solid var(--color-border);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-sm);
        }

        .pagination-info {
          font-size: var(--font-xs);
          color: var(--color-text-muted);
        }

        .custom-pagination {
          margin: 0;
          gap: 3px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .custom-pagination .page-link {
          background: #ffffff !important;
          border-color: var(--color-border) !important;
          color: var(--color-text-muted) !important;
          border-radius: var(--radius-sm) !important;
          min-width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.5rem !important;
          font-size: var(--font-sm);
          transition: all 0.2s;
        }

        .custom-pagination .page-link:hover {
          background: #f0f4fb !important;
          border-color: var(--color-primary) !important;
          color: var(--color-text) !important;
        }

        .custom-pagination .page-item.active .page-link {
          background: var(--color-primary) !important;
          border-color: var(--color-primary) !important;
          color: white !important;
        }

        .custom-pagination .page-item.disabled .page-link {
          opacity: 0.4;
        }

        /* ═══════════════════════════════════════════════════════════════════
           MODALS
        ═══════════════════════════════════════════════════════════════════ */
        .modal-content {
          background: #ffffff !important;
          border: 1px solid var(--color-border) !important;
          border-radius: var(--radius-xl) !important;
          color: var(--color-text) !important;
        }

        .modal-header {
          border-bottom: 1px solid var(--color-border) !important;
          padding: var(--space-md) !important;
        }

        .modal-title {
          font-size: var(--font-lg) !important;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .modal-body {
          padding: var(--space-md) !important;
        }

        .modal-footer {
          border-top: 1px solid var(--color-border) !important;
          padding: var(--space-md) !important;
          gap: var(--space-sm);
        }

        .form-control, .form-select {
          background: #ffffff !important;
          border: 1px solid var(--color-border) !important;
          color: var(--color-text) !important;
          border-radius: var(--radius-sm) !important;
          padding: var(--space-sm) !important;
          font-size: var(--font-sm) !important;
        }

        .form-control:focus, .form-select:focus {
          border-color: var(--color-primary) !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
        }

        .form-label {
          color: var(--color-text-muted) !important;
          font-size: var(--font-sm) !important;
          font-weight: 500 !important;
          margin-bottom: 0.35rem !important;
        }

        .form-control::placeholder {
          color: #64748b !important;
        }

        .form-control.is-invalid {
          border-color: var(--color-danger) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           TOAST & OFFCANVAS
        ═══════════════════════════════════════════════════════════════════ */
        .toast-container-custom {
          position: fixed !important;
          top: max(1rem, env(safe-area-inset-top));
          right: 1rem;
          left: 1rem;
          z-index: 9999;
        }

        .toast {
          border-radius: var(--radius-md) !important;
          border: none !important;
          max-width: 100%;
        }

        .offcanvas {
          background: #ffffff !important;
          border-left: 1px solid var(--color-border) !important;
        }

        .offcanvas-header {
          border-bottom: 1px solid var(--color-border);
        }

        .offcanvas-title {
          color: var(--color-text);
          font-weight: 600;
        }

        .btn-close {
          filter: invert(1);
        }

        /* ═══════════════════════════════════════════════════════════════════
           SCROLLBAR
        ═══════════════════════════════════════════════════════════════════ */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #ffffff;
        }

        ::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #e2e8f0;
        }

        * {
          scrollbar-width: thin;
          scrollbar-color: #e2e8f0 #ffffff;
        }

        /* ═══════════════════════════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS
        ═══════════════════════════════════════════════════════════════════ */

        /* Extra Small (< 360px) - iPhone SE, Small Android */
        @media (max-width: 359px) {
          .app-container {
            padding: 0.5rem;
          }

          .stats-grid {
            gap: 0.35rem;
          }

          .stat-card {
            padding: 0.5rem;
          }

          .stat-icon {
            width: 32px;
            height: 32px;
          }

          .stat-value {
            font-size: 1.1rem;
          }

          .stat-label {
            font-size: 0.55rem;
          }

          .header-actions {
            flex-direction: column;
          }

          .header-actions > * {
            width: 100%;
            justify-content: center;
          }

          .mobile-filter-btn {
            display: flex !important;
          }

          .desktop-filters {
            display: none !important;
          }
        }

        /* Small Phones (360px - 479px) */
        @media (min-width: 360px) and (max-width: 479px) {
          .mobile-filter-btn {
            display: flex !important;
          }

          .desktop-filters {
            display: none !important;
          }

          .stat-value {
            font-size: 1.3rem;
          }
        }

        /* Medium Phones (480px - 575px) */
        @media (min-width: 480px) and (max-width: 575px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .stat-card {
            flex-direction: column;
            text-align: center;
            padding: 0.5rem;
          }

          .stat-icon {
            width: 36px;
            height: 36px;
            margin-bottom: 0.25rem;
          }

          .stat-value {
            font-size: 1.2rem;
          }

          .stat-label {
            font-size: 0.5rem;
          }

          .mobile-filter-btn {
            display: flex !important;
          }

          .desktop-filters {
            display: none !important;
          }

          .mobile-cards-container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: var(--space-sm);
          }

          .mobile-data-card {
            margin-bottom: 0;
          }
        }

        /* Large Phones / Small Tablets (576px - 767px) */
        @media (min-width: 576px) and (max-width: 767px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .mobile-filter-btn {
            display: flex !important;
          }

          .desktop-filters {
            display: none !important;
          }

          .view-toggle {
            display: flex;
          }
        }

        /* Tablets (768px - 1023px) */
        @media (min-width: 768px) and (max-width: 1023px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .header-top {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .header-actions {
            margin-top: 0;
          }

          .mobile-filter-btn {
            display: none !important;
          }

          .desktop-filters {
            display: block !important;
          }

          .view-toggle {
            display: flex;
          }
        }

        /* Desktop (1024px - 1439px) */
        @media (min-width: 1024px) {
          .app-container {
            padding: 1.5rem 2rem;
          }

          .stats-grid {
            gap: 1rem;
          }

          .stat-card {
            padding: 1rem;
          }

          .stat-value {
            font-size: 1.75rem;
          }

          .header-top {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .mobile-filter-btn {
            display: none !important;
          }

          .view-toggle {
            display: flex;
          }
        }

        /* Large Desktop (1440px+) */
        @media (min-width: 1440px) {
          .app-container {
            max-width: 1600px;
            margin: 0 auto;
            padding: 2rem 3rem;
          }

          .stat-value {
            font-size: 2rem;
          }

          .stat-label {
            font-size: 0.8rem;
          }
        }

        /* Ultra Wide (1920px+) */
        @media (min-width: 1920px) {
          .app-container {
            max-width: 1800px;
          }
        }

        /* Landscape Mode */
        @media (orientation: landscape) and (max-height: 500px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .stat-card {
            flex-direction: row;
            padding: 0.5rem;
          }

          .mobile-cards-container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Touch Devices */
        @media (hover: none) and (pointer: coarse) {
          .btn-action {
            width: 44px !important;
            height: 44px !important;
          }

          .custom-pagination .page-link {
            min-width: 44px;
            height: 44px;
          }

          .form-check-input {
            width: 22px !important;
            height: 22px !important;
          }

          .stat-card:hover {
            transform: none;
          }

          .btn-primary-modern:hover {
            transform: none;
          }
        }

        /* High DPI Displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .data-container,
          .stat-card,
          .filter-card,
          .mobile-data-card,
          .tablet-list-item,
          .ultra-compact-card {
            border-width: 0.5px;
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Print */
        @media print {
          .app-container {
            background: white !important;
            color: black !important;
          }

          .btn-primary-modern,
          .btn-outline-modern,
          .btn-action,
          .mobile-filter-btn,
          .bulk-bar,
          .pagination-wrapper,
          .header-actions,
          .mobile-card-actions,
          .ucc-actions,
          .tli-actions {
            display: none !important;
          }
        }
      `}</style>

      {/* Toast */}
      <ToastContainer position="top-end" className="toast-container-custom">
        <Toast
          bg={toast.type === "error" ? "danger" : "success"}
          show={toast.show}
          onClose={() => setToast({ ...toast, show: false })}
          autohide
          delay={3500}
        >
          <Toast.Body className="text-white fw-medium">
            <i className={`bi ${toast.type === "error" ? "bi-exclamation-circle" : "bi-check-circle"} me-2`}></i>
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="header-section">
        <div className="header-top">
          <div>
            <h2 className="header-title">
              <i className="bi bi-list-check"></i>
              Imibereho Myiza
            </h2>
            <p className="header-subtitle">
              {deviceInfo.width < 400 
                ? "Gucunga ibikorwa"
                : "Kwandika no gukurikirana ibikorwa birigukorwa"}
            </p>
          </div>

          <div className="header-actions">
            <Button className="btn-primary-modern" onClick={handleAdd}>
              <i className="bi bi-plus-circle"></i>
              <span className="d-none d-sm-inline">Ongeramo</span>
            </Button>

            <Button className="btn-outline-modern" onClick={loadData}>
              <i className="bi bi-arrow-clockwise"></i>
            </Button>

            <Button className="btn-outline-modern" onClick={exportToCSV}>
              <i className="bi bi-download"></i>
              <span className="d-none d-md-inline">Excel</span>
            </Button>

            <Button 
              className="btn-outline-modern mobile-filter-btn"
              onClick={() => setShowMobileFilters(true)}
            >
              <i className="bi bi-funnel"></i>
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className="bulk-bar">
          <div className="bulk-info">
            <i className="bi bi-check2-square" style={{ color: theme.primary }}></i>
            <span>{selectedItems.length} byatoranyijwe</span>
          </div>
          <div className="bulk-actions">
            <Button variant="danger" size="sm" onClick={handleBulkDelete}>
              <i className="bi bi-trash me-1"></i>
              <span className="d-none d-sm-inline">Siba</span>
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => setSelectedItems([])}>
              <i className="bi bi-x"></i>
            </Button>
          </div>
        </div>
      )}

      {/* Stats */}
      {renderStats()}

      {/* Desktop Filters */}
      <div className="filter-section desktop-filters">
        <div className="filter-card">
          <div className="search-container">
            <div className="search-input-wrapper">
              <i className="bi bi-search search-icon"></i>
              <Form.Control
                placeholder="Shakisha..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="search-input"
              />
            </div>

            <Form.Select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="filter-select"
            >
              <option value="all">Byose</option>
              <option value="Iri_gukorwa">Biracyakorwa</option>
              <option value="Yarangiye">Byarangiye</option>
            </Form.Select>

            <Form.Select
              value={itemsPerPage}
              onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="filter-select"
              style={{ minWidth: '80px' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Form.Select>

            <Button className="btn-outline-modern" onClick={resetFilters}>
              <i className="bi bi-arrow-repeat"></i>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Filters Offcanvas */}
      <Offcanvas 
        show={showMobileFilters} 
        onHide={() => setShowMobileFilters(false)} 
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="bi bi-funnel me-2"></i>Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="d-flex flex-column gap-3">
            <div>
              <Form.Label>Shakisha</Form.Label>
              <div className="search-input-wrapper" style={{ position: 'relative' }}>
                <i className="bi bi-search search-icon"></i>
                <Form.Control
                  placeholder="Izina, ID..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="search-input"
                />
              </div>
            </div>

            <div>
              <Form.Label>Imiterere</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="filter-select"
              >
                <option value="all">Byose</option>
                <option value="Iri_gukorwa">Biracyakorwa</option>
                <option value="Yarangiye">Byarangiye</option>
              </Form.Select>
            </div>

            <div>
              <Form.Label>Ibintu kuri paji</Form.Label>
              <Form.Select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="filter-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
              </Form.Select>
            </div>

            <div className="d-grid gap-2 mt-3">
              <Button className="btn-primary-modern" onClick={() => setShowMobileFilters(false)}>
                <i className="bi bi-check me-2"></i>Emeza
              </Button>
              <Button className="btn-outline-modern" onClick={resetFilters}>
                <i className="bi bi-arrow-repeat me-2"></i>Reset
              </Button>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Data Container */}
      <div className="data-container">
        <div className="data-header">
          <h5 className="data-header-title">
            <i className="bi bi-table"></i>
            <span className="d-none d-sm-inline">Urutonde</span>
          </h5>
          <div className="data-header-meta">
            <span className="results-count">{filteredData.length} ibyabonetse</span>
            <div className="view-toggle">
              <button 
                className={`view-toggle-btn ${viewMode === 'auto' || viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                <i className="bi bi-table"></i>
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                <i className="bi bi-grid"></i>
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <i className="bi bi-list-ul"></i>
              </button>
            </div>
          </div>
        </div>

        {renderDataView()}
        {renderPagination()}
      </div>

      {/* Add/Edit Modal */}
      <Modal 
        show={show} 
        onHide={() => setShow(false)} 
        size={deviceInfo.isMobile ? undefined : "lg"} 
        centered
        fullscreen={deviceInfo.width < 576 ? true : undefined}
      >
        <Modal.Header 
          closeButton 
          closeVariant="white" 
          style={{ 
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`, 
            border: "none" 
          }}
        >
          <Modal.Title>
            <i className={`bi ${editingId ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
            {editingId ? "Hindura" : "Ongeramo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col xs={12} md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Umuturage *</Form.Label>
                  <Form.Select
                    name="muturage_id"
                    value={form.muturage_id}
                    onChange={handleCitizenSelect}
                    isInvalid={!!formErrors.muturage_id}
                  >
                    <option value="">-- Hitamo umuturage --</option>
                    {citizens.map((c) => (
                      <option key={c.umuturage_id} value={c.umuturage_id}>
                        {c.full_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.muturage_id}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Ikibazo / Gahunda *</Form.Label>
              <Form.Control
                name="gahunda_yafashwemo"
                value={form.gahunda_yafashwemo}
                onChange={handleChange}
                isInvalid={!!formErrors.gahunda_yafashwemo}
                placeholder="Andika ikibazo..."
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.gahunda_yafashwemo}
              </Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Itariki *</Form.Label>
                  <Form.Control
                    type="date"
                    name="itariki_yatangiranye"
                    value={form.itariki_yatangiranye}
                    onChange={handleChange}
                    isInvalid={!!formErrors.itariki_yatangiranye}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.itariki_yatangiranye}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Imiterere</Form.Label>
                  <Form.Select
                    name="imiterere_yayo"
                    value={form.imiterere_yayo}
                    onChange={handleChange}
                  >
                    <option value="Iri_gukorwa">Iri gukorwa</option>
                    <option value="Yarangiye">Yarangiye</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Ibisobanuro</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="ibisobanuro"
                value={form.ibisobanuro}
                onChange={handleChange}
                placeholder="Andika ibisobanuro..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            <i className="bi bi-x-circle me-1"></i>Funga
          </Button>
          <Button
            style={{ 
              background: editingId 
                ? `linear-gradient(135deg, ${theme.warning} 0%, #d97706 100%)` 
                : `linear-gradient(135deg, ${theme.success} 0%, #059669 100%)`, 
              border: "none"
            }}
            onClick={handleSubmit}
          >
            <i className={`bi me-1 ${editingId ? "bi-save" : "bi-check-circle"}`}></i>
            {editingId ? "Hindura" : "Bika"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header 
          closeButton 
          closeVariant="white" 
          style={{ 
            background: `linear-gradient(135deg, ${theme.danger} 0%, #dc2626 100%)`, 
            border: "none" 
          }}
        >
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Emeza Gusiba
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-trash" style={{ fontSize: '3rem', color: theme.danger }}></i>
            <p className="mt-3 mb-0">
              Urashaka gusiba iki kintu?<br/>
              <strong style={{ color: theme.danger }}>Ntikizigaruka!</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Hagarika
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="bi bi-trash me-1"></i>Siba
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ImiberehoMyiza;